import React, { useState, useEffect } from 'react';
import { doc, deleteDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { Package, Plus, Search, Edit, Trash2, Tag, Cog, X } from 'lucide-react';
import BrandForm from './BrandForm';
import SearchCounter from '../SearchCounter';
import BrandBadge from '../BrandBadge';
import { useAuth } from '../../contexts/AuthContext';
import { getAllBrands, checkBrandInUse } from '../../lib/brandService';
import { getAllTypes } from '../../lib/typeService';
import { matchesSearchTerm } from '../../lib/searchUtils';

const BrandsTab = () => {
  const { userRole } = useAuth();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [tiposEquipo, setTiposEquipo] = useState([]);
  const [expandedBrands, setExpandedBrands] = useState(new Set());

  useEffect(() => {
    fetchBrands();
    fetchTiposEquipo();
  }, []);

  const fetchBrands = async () => {
    try {
      setLoading(true);
      
      // Use local-first brand service
      const brandsList = await getAllBrands();
      setBrands(brandsList);
    } catch (error) {
      console.error('Error fetching brands:', error);
      console.log('Marcas collection may not exist yet, starting with empty list');
      setBrands([]);
    } finally {
      setLoading(false);
    }
  };


  const fetchTiposEquipo = async () => {
    try {
      // Use local-first type service
      const tiposList = await getAllTypes();
      setTiposEquipo(tiposList);
    } catch (error) {
      console.log('Tipos_equipo collection may not exist yet, starting with empty list');
      setTiposEquipo([]);
    }
  };

  const getTipoNameById = (tipoId) => {
    const tipo = tiposEquipo.find(t => t.id === tipoId);
    return tipo ? tipo.nombre : 'Tipo desconocido';
  };

  const filteredBrands = brands.filter(brand => {
    // If no search term, include all brands
    if (!searchTerm || searchTerm.trim() === '') {
      return true;
    }
    
    // Search by name
    if (brand.nombre && matchesSearchTerm(brand.nombre, searchTerm)) {
      return true;
    }
    
    // Search by equipment types (new format)
    if (brand.tiposEquipoIds && Array.isArray(brand.tiposEquipoIds)) {
      return brand.tiposEquipoIds.some(tipoId => {
        const tipoName = getTipoNameById(tipoId);
        return matchesSearchTerm(tipoName, searchTerm);
      });
    }
    
    // Search by equipment types (old format for backward compatibility)
    if (brand.tiposEquipo && Array.isArray(brand.tiposEquipo)) {
      return brand.tiposEquipo.some(tipo => 
        matchesSearchTerm(tipo, searchTerm)
      );
    }
    
    return false;
  });

  const canEdit = () => {
    return userRole && ['admin', 'shopkeeper'].includes(userRole);
  };

  const canDelete = () => {
    return userRole === 'admin';
  };

  const canPerformActions = () => {
    return userRole && ['admin', 'shopkeeper'].includes(userRole);
  };

  const handleFormSuccess = async () => {
    const message = editingBrand ? 'Marca actualizada exitosamente' : 'Marca creada exitosamente';
    setSuccess(message);
    setEditingBrand(null);
    await fetchBrands(); // Refresh brands with updated counts (cache was already refreshed in BrandForm)
    setTimeout(() => setSuccess(''), 5000);
  };

  const handleEdit = (brand) => {
    setEditingBrand(brand);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingBrand(null);
  };


  const handleDelete = async (brand) => {
    if (!canDelete()) {
      setError('No tienes permisos para eliminar marcas');
      setTimeout(() => setError(''), 5000);
      return;
    }

    try {
      // Check if brand is used in equipment
      const isInUse = await checkBrandInUse(brand.nombre);
      
      if (isInUse) {
        setError(`No se puede eliminar la marca "${brand.nombre}" porque está siendo usada en el catálogo de equipos.`);
        setTimeout(() => setError(''), 8000);
        return;
      }

      // Confirm deletion
      if (!window.confirm(
        `¿Está seguro de eliminar permanentemente la marca "${brand.nombre}"?\n\n` +
        `Esta acción no se puede deshacer.`
      )) {
        return;
      }

      // Delete the brand
      await deleteDoc(doc(db, 'marcas', brand.id));
      setSuccess(`Marca "${brand.nombre}" eliminada exitosamente`);
      fetchBrands();
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Error deleting brand:', error);
      setError('Error al eliminar la marca');
      setTimeout(() => setError(''), 5000);
    }
  };

  const formatEquipmentTypes = (brand) => {
    // Handle new format (tiposEquipoIds array)
    if (brand.tiposEquipoIds && Array.isArray(brand.tiposEquipoIds)) {
      if (brand.tiposEquipoIds.length === 0) return 'Sin tipos definidos';
      
      const tipoNames = brand.tiposEquipoIds.map(id => getTipoNameById(id));
      
      if (tipoNames.length <= 3) {
        return tipoNames.join(', ');
      }
      
      return `${tipoNames.slice(0, 3).join(', ')} y ${tipoNames.length - 3} más`;
    }
    
    // Handle old format (tiposEquipo array) for backward compatibility
    if (brand.tiposEquipo && Array.isArray(brand.tiposEquipo)) {
      if (brand.tiposEquipo.length === 0) return 'Sin tipos definidos';
      
      if (brand.tiposEquipo.length <= 3) {
        return brand.tiposEquipo.join(', ');
      }
      
      return `${brand.tiposEquipo.slice(0, 3).join(', ')} y ${brand.tiposEquipo.length - 3} más`;
    }
    
    return 'Sin tipos definidos';
  };

  const getEquipmentTypeTags = (brand) => {
    // Handle new format (tiposEquipoIds array)
    if (brand.tiposEquipoIds && Array.isArray(brand.tiposEquipoIds)) {
      return brand.tiposEquipoIds.slice(0, 2).map(id => ({
        id,
        nombre: getTipoNameById(id)
      }));
    }
    
    // Handle old format (tiposEquipo array) for backward compatibility
    if (brand.tiposEquipo && Array.isArray(brand.tiposEquipo)) {
      return brand.tiposEquipo.slice(0, 2).map((tipo, index) => ({
        id: `legacy-${index}`,
        nombre: tipo
      }));
    }
    
    return [];
  };

  const getEquipmentTypeCount = (brand) => {
    if (brand.tiposEquipoIds && Array.isArray(brand.tiposEquipoIds)) {
      return brand.tiposEquipoIds.length;
    }
    if (brand.tiposEquipo && Array.isArray(brand.tiposEquipo)) {
      return brand.tiposEquipo.length;
    }
    return 0;
  };

  const toggleExpanded = (brandId) => {
    const newExpanded = new Set(expandedBrands);
    if (newExpanded.has(brandId)) {
      newExpanded.delete(brandId);
    } else {
      newExpanded.add(brandId);
    }
    setExpandedBrands(newExpanded);
  };

  const getAllEquipmentTypeTags = (brand) => {
    // Handle new format (tiposEquipoIds array)
    if (brand.tiposEquipoIds && Array.isArray(brand.tiposEquipoIds)) {
      return brand.tiposEquipoIds.map(id => ({
        id,
        nombre: getTipoNameById(id)
      }));
    }
    
    // Handle old format (tiposEquipo array) for backward compatibility
    if (brand.tiposEquipo && Array.isArray(brand.tiposEquipo)) {
      return brand.tiposEquipo.map((tipo, index) => ({
        id: `legacy-${index}`,
        nombre: tipo
      }));
    }
    
    return [];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Tag className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600 dark:text-blue-400 flex-shrink-0" />
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">Marcas</h2>
        </div>
        {canEdit() && (
          <button 
            onClick={() => setShowForm(true)}
            className="flex items-center justify-center w-10 h-10 bg-blue-600 dark:bg-blue-700 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
            title="Nueva Marca"
          >
            <Plus className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Search Bar */}
      <div className="space-y-4">
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-md">
            {error}
          </div>
        )}
        
        {success && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 px-4 py-3 rounded-md">
            {success}
          </div>
        )}
        
        <div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              placeholder="Buscar marcas por nombre o tipos de equipo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                title="Limpiar búsqueda"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <SearchCounter 
            count={filteredBrands.length} 
            label="marcas" 
            isSearching={!!searchTerm} 
          />
        </div>
      </div>

      {/* Brands List */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
        {loading ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            Cargando marcas...
          </div>
        ) : filteredBrands.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            {searchTerm ? 'No se encontraron marcas que coincidan con la búsqueda' : 'No hay marcas registradas'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Marca
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden lg:table-cell">
                    Tipos de Equipo
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden md:table-cell">
                    Equipos en Catálogo
                  </th>
                  {canPerformActions() && (
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Acciones
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredBrands.map((brand) => (
                  <tr key={brand.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-3 sm:px-6 py-4">
                      <div className="flex items-center">
                        <Tag className="h-5 w-5 text-blue-500 dark:text-blue-400 mr-3 flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="flex items-center space-x-2">
                            <BrandBadge brand={brand} size="md" />
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                            Creada: {new Date(brand.createdAt?.toDate ? brand.createdAt.toDate() : brand.createdAt).toLocaleDateString('es-ES')}
                          </div>
                          {/* Mobile: Show equipment types below name when column is hidden */}
                          <div className="mt-2 lg:hidden">
                            {getEquipmentTypeCount(brand) > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {(expandedBrands.has(brand.id) ? getAllEquipmentTypeTags(brand) : getEquipmentTypeTags(brand)).map((tipo) => (
                                  <span
                                    key={tipo.id}
                                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-400"
                                  >
                                    <Cog className="h-3 w-3 mr-1" />
                                    {tipo.nombre}
                                  </span>
                                ))}
                                {getEquipmentTypeCount(brand) > 2 && !expandedBrands.has(brand.id) && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleExpanded(brand.id);
                                    }}
                                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/30 cursor-pointer transition-colors"
                                    title="Ver todos los tipos de equipo"
                                  >
                                    +{getEquipmentTypeCount(brand) - 2}
                                  </button>
                                )}
                                {expandedBrands.has(brand.id) && getEquipmentTypeCount(brand) > 2 && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleExpanded(brand.id);
                                    }}
                                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/30 cursor-pointer transition-colors"
                                    title="Mostrar menos"
                                  >
                                    −
                                  </button>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-500 dark:text-gray-400">Sin tipos definidos</span>
                            )}
                          </div>
                          {/* Mobile: Show equipment count below when column is hidden */}
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 md:hidden">
                            {brand.equipmentCount || 0} equipos en catálogo
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4 hidden lg:table-cell">
                      {getEquipmentTypeCount(brand) > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {(expandedBrands.has(brand.id) ? getAllEquipmentTypeTags(brand) : getEquipmentTypeTags(brand)).map((tipo) => (
                            <span
                              key={tipo.id}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-400"
                            >
                              <Cog className="h-3 w-3 mr-1" />
                              {tipo.nombre}
                            </span>
                          ))}
                          {getEquipmentTypeCount(brand) > 2 && !expandedBrands.has(brand.id) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleExpanded(brand.id);
                              }}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/30 cursor-pointer transition-colors"
                              title="Ver todos los tipos de equipo"
                            >
                              +{getEquipmentTypeCount(brand) - 2}
                            </button>
                          )}
                          {expandedBrands.has(brand.id) && getEquipmentTypeCount(brand) > 2 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleExpanded(brand.id);
                              }}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/30 cursor-pointer transition-colors"
                              title="Mostrar menos"
                            >
                              −
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500 dark:text-gray-400">Sin tipos definidos</span>
                      )}
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap hidden md:table-cell">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400">
                        {brand.equipmentCount || 0} equipos
                      </span>
                    </td>
                    {canPerformActions() && (
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          {canEdit() && (
                            <button
                              onClick={() => handleEdit(brand)}
                              className="p-2 text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                              title="Editar marca"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          )}
                          {canDelete() && (
                            <button
                              onClick={() => handleDelete(brand)}
                              className={`p-2 rounded ${
                                brand.equipmentCount > 0
                                  ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                                  : 'text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20'
                              }`}
                              title={
                                brand.equipmentCount > 0
                                  ? `No se puede eliminar - está siendo usada en ${brand.equipmentCount} equipo(s)`
                                  : 'Eliminar marca'
                              }
                              disabled={brand.equipmentCount > 0}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Brand Form Modal */}
      <BrandForm
        isOpen={showForm}
        onClose={handleCloseForm}
        onSuccess={handleFormSuccess}
        editingBrand={editingBrand}
      />
    </div>
  );
};

export default BrandsTab;